import { useEffect, useRef, useState } from 'react';
import { useRadarLocale } from '@/i18n/RadarLocaleContext';
import { radarApi } from './radarApi';
import { RadarEmailSignalNotice } from './RadarEmailSignalNotice';
import type { RadarDraft } from './types';

type TokenResponse = { access_token?: string; error?: string; expires_in?: number; scope?: string };
type TokenClient = { requestAccessToken: (options: { prompt: string }) => void };
type GoogleSdk = { accounts: { oauth2: {
  initTokenClient: (config: { client_id: string; scope: string; callback: (r:TokenResponse)=>void; error_callback: ()=>void }) => TokenClient;
  initCodeClient?: (config: { client_id: string; scope: string; ux_mode: 'popup'; callback: (r:{code?:string;error?:string})=>void; error_callback: ()=>void }) => { requestCode: ()=>void };
} } };
const clientId = import.meta.env.VITE_RADAR_GOOGLE_CLIENT_ID as string | undefined;
let sdkPromise: Promise<GoogleSdk> | undefined;
export function loadSdk() {
  return sdkPromise ||= new Promise<GoogleSdk>((resolve,reject) => {
    const win=window as Window & {google?: GoogleSdk};
    if(win.google) { resolve(win.google); return; }
    const script=document.createElement('script');script.src='https://accounts.google.com/gsi/client';script.async=true;
    script.onload=()=>win.google ? resolve(win.google) : reject(new Error('Gmail authorization is unavailable.'));
    script.onerror=()=>{sdkPromise=undefined;reject(new Error('Gmail authorization is unavailable.'));};
    document.head.appendChild(script);
  });
}

type DraftRef = Pick<RadarDraft,'id'|'version'> & {subject?:string};
export function RadarGmailDraft({token,workspaceId,draft,drafts,disabled,onSaved,beforeSave,compact=false}:{token:string;workspaceId:string;draft?:DraftRef;drafts?:DraftRef[];disabled:boolean;onSaved?:()=>void;compact?:boolean;beforeSave?:()=>Promise<string|void>}) {
  const { t, e }=useRadarLocale();
  const [sdk,setSdk]=useState<GoogleSdk | null>(null);
  const [account,setAccount]=useState<{email:string;token:string;expiresAt:number} | null>(null);
  const [busy,setBusy]=useState(false);
  const [receipts,setReceipts]=useState<Record<string,{done:boolean;error?:string}>>({});
  const entries=drafts || (draft ? [draft] : []);
  const complete=entries.length>0 && entries.every(d=>receipts[d.id+':'+d.version]?.done);
  const receiptRef=useRef(receipts);receiptRef.current=receipts;
  const [error,setError]=useState('');
  const [saved,setSaved]=useState<{url:string;account_email:string}|null>(null);
  const alive=useRef(true); const lock=useRef(false);
  useEffect(()=>{alive.current=true;if(clientId)loadSdk().then(s=>{if(alive.current)setSdk(s);}).catch(err=>{if(alive.current)setError(err.message);});return()=>{alive.current=false;};},[]);
  useEffect(()=>{if(draft){setSaved(null);setError('');}},[draft?.id,draft?.version]);
  function connect() {
    if(!sdk || !clientId || lock.current)return;
    lock.current=true;setBusy(true);setError('');
    const finish=()=>{lock.current=false;if(alive.current)setBusy(false);};
    sdk.accounts.oauth2.initTokenClient({client_id:clientId,scope:'openid email https://www.googleapis.com/auth/gmail.compose',
      error_callback:()=>{if(alive.current)setError('Gmail authorization was cancelled.');finish();},
      callback:async response=>{
        try {
          if(!response.access_token || response.error || !response.scope?.split(' ').includes('https://www.googleapis.com/auth/gmail.compose'))throw new Error('Gmail draft permission is required.');
          const result=await fetch('https://www.googleapis.com/oauth2/v3/userinfo',{headers:{Authorization:'Bearer '+response.access_token}});
          if(!result.ok)throw new Error('Reconnect Gmail and confirm the account before saving.');
          const identity=await result.json();
          if(!identity.email || identity.email_verified!==true)throw new Error('Reconnect Gmail and confirm the account before saving.');
          if(alive.current){setAccount({email:identity.email,token:response.access_token,expiresAt:Date.now()+((response.expires_in || 3600)-60)*1000});if(identity.email!==account?.email){setReceipts({});setSaved(null);}}
        }catch(err){if(alive.current)setError(err instanceof Error ? err.message : 'Gmail authorization is unavailable.');}
        finally{finish();}
      }}).requestAccessToken({prompt:'select_account'});
  }
  async function save() {
    if(!account || lock.current)return;
    if(account.expiresAt<Date.now()){setAccount(null);setError('Reconnect Gmail and confirm the account before saving.');return;}
    lock.current=true;setBusy(true);setError('');
    try {
      // Capture selected, saved versions once. A failure affects only that item.
      const selection=[...entries];
      // Optional history check before the batch. If it says to wait, or cannot run,
      // nothing is saved; the server gate alone would decide on stale history.
      if(beforeSave) {
        let stop:string|void;
        try{stop=await beforeSave();}catch{stop='Contact history could not be checked, so nothing was saved. Try again in a moment.';}
        if(stop){if(alive.current)setError(stop);return;}
      }
      for(const item of selection) {
        if(!alive.current)break;
        const itemKey=item.id+':'+item.version;
        if(drafts && receiptRef.current[itemKey]?.done)continue;
        if(account.expiresAt<Date.now()){setError('Reconnect Gmail and confirm the account before saving.');break;}
        try {
          const result=await radarApi.gmailDraft(token,workspaceId,item.id,item.version,account.token,account.email);
          if(alive.current){setSaved(result);setReceipts(r=>({...r,[itemKey]:{done:true}}));}
        } catch(err) {
          const failure=err instanceof Error?err.message:'Gmail save could not be confirmed.';
          if(alive.current){setReceipts(r=>({...r,[itemKey]:{done:false,error:failure}}));if(!drafts)setError(failure);}
        }
      }
      if(alive.current)onSaved?.();
    } finally{lock.current=false;if(alive.current)setBusy(false);}
  }
  if(compact)return <div className="radar-gmail-compact" aria-label="Gmail export">
    <div className="radar-gmail-controls">
      <button className="radar-gmail-account" disabled={!clientId || !sdk || busy} onClick={connect}
        title={account?'Change Gmail account':'Connect your Gmail account'}>{account?account.email:'Connect Gmail'}</button>
      <button className="radar-primary-action" disabled={!clientId || !account || busy || disabled || !entries.length || complete}
        onClick={()=>void save()}>{busy?'Saving…':`Save to Gmail${entries.length?` (${entries.length})`:''}`}</button>
    </div>
    <div className="radar-gmail-caption">
      {saved?<span role="status">Saved · <a href={saved.url} target="_blank" rel="noreferrer">Open Gmail drafts</a></span>
        :<span>{!clientId?'Gmail connection unavailable':'Drafts only · Nothing is sent'}</span>}
    </div>
    <RadarEmailSignalNotice/>
    {entries.filter(item=>receipts[item.id+':'+item.version]?.error).map(item=><p role="alert" key={item.id}>{item.subject || 'Email'}: {receipts[item.id+':'+item.version].error}</p>)}
    {error&&<p role="alert">{e(error)}</p>}
  </div>;
  return <div className="radar-act-gmail"><h3>{t('Optional: save to Gmail')}</h3>
    <p className="radar-muted">{t('Creates a draft in the account you choose. Nothing is sent; existing Gmail edits are preserved.')}</p>
    <RadarEmailSignalNotice/>
    {!clientId ? <p className="radar-muted">{t('Gmail connection is not configured yet. Copy your draft to use it anywhere.')}</p> : <>
      {drafts && !entries.length && !saved && <p className="radar-muted">{t('Select a ready draft above to choose a Gmail account.')}</p>}
      {!account && entries.length > 0 && <p className="radar-muted">{t('First choose your sending Gmail account. Then save the selected drafts; nothing is sent.')}</p>}
      <button className="radar-secondary-action" disabled={!sdk || busy || disabled} onClick={connect}>{t(account ? 'Choose another Gmail account' : 'Choose a Gmail account')}</button>
      {account && <><p>{t('Gmail account')} · <strong>{account.email}</strong></p><button className="radar-primary-action" disabled={busy || disabled || (drafts ? complete : !!saved)} onClick={()=>void save()}>{drafts ? `Save selected drafts (${entries.length})` : t('Save draft to this Gmail account')}</button></>}
      {drafts && <ul>{entries.filter(item=>receipts[item.id+':'+item.version]).map(item=>{const r=receipts[item.id+':'+item.version];return <li key={item.id} role={r.error?'alert':'status'}>{item.subject || 'Email'}: {r.done?'Saved to Gmail':r.error}</li>;})}</ul>}
      {saved && <p role="status"><a className="radar-contact-link" href={saved.url} target="_blank" rel="noreferrer">{t('Open Gmail drafts')} · {saved.account_email}</a></p>}
    </>}{error && <p role="alert">{e(error)}</p>}
  </div>;
}
